import React, { useEffect, useMemo, useState } from 'react';
import Logo from '../assets/images/onlylogo_kali.svg';
import LogoWhite from '../assets/images/only_logo_white.svg';
import { ReactComponent as SendIcon } from '../assets/images/SendIcon.svg';
import { ReactComponent as DisabledSendIcon } from '../assets/images/disabled-send-icon.svg';
import ReactionBar from '../components/ReactionBar';
import { useLocation, useOutletContext } from 'react-router-dom';
import {
  Avatar,
  Box,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Paper,
  Chip,
  Grid,
  Stack,
  styled,
  useTheme,
  Button,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import TypingIndicator from './TypingIndicator';
import ReactMarkdown from 'react-markdown';
import { questionTemplates } from '../const';
import { useAuth } from '../context/AuthContext';

const InputTextBoxGroup = ({
  input,
  setInput,
  category,
  setCategory,
  sendMessage,
  handleTemplateClick,
  Icon,
  secondaryMode,
  isThinking,
}) => {
  const theme = useTheme();
  const { isLogin } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  let templatesToRender = questionTemplates[category];
  if (secondaryMode) {
    templatesToRender = templatesToRender.slice(0, 3);
  }

  const chipsContainer =
    secondaryMode && isMobile ? null : (
      <Grid container spacing={1} sx={{ mt: 2, opacity: '70%' }}>
        {templatesToRender.map((q, idx) => {
          const disabled = !isLogin; // Disable if not logged in
          return (
            <Grid item key={idx}>
              <Chip
                label={q}
                variant="outlined"
                disabled={disabled}
                onClick={() => handleTemplateClick(q)}
                sx={{ cursor: 'pointer', borderRadius: '4px' }}
              />
            </Grid>
          );
        })}
      </Grid>
    );

  const pinToBottomStyles = secondaryMode
    ? {
        position: 'absolute',
        bottom: '2px',
        right: 0,
        left: 0,
      }
    : {};

  return (
    <Box sx={{ ...pinToBottomStyles }}>
      {secondaryMode && chipsContainer}
      <Paper
        variant="outlined"
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 3,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '2px', // thickness of border
            background:
              'linear-gradient(to right, transparent, #75B5FF 10%, #04346D 90%, transparent)', // gradient color
          },
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={6}
          placeholder="Ask me astrology related queries…."
          variant="standard"
          value={input}
          onChange={(e) => {
            if (!isLogin) return; // Prevent input if not logged in
            setInput(e.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              sendMessage();
              event.preventDefault(); // optional: prevent form submission if inside a <form>
            }
          }}
          slotProps={{
            input: {
              readOnly: !isLogin,
              disableUnderline: true,
              sx: { fontSize: 16 },
            },
          }}
          helperText={
            !isLogin ? (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  fontStyle: 'italic',
                  gap: 1,
                  mt: 0.5,
                }}
              >
                ✨ This is your free question. Submit to see your personalized
                insight.
              </Typography>
            ) : (
              ''
            )
          }
        />

        <Box
          mt={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            variant="standard"
            sx={{ minWidth: 90, fontSize: 14 }}
            disableUnderline
          >
            {Object.keys(questionTemplates).map((cat) =>
              cat !== 'Marriage' ? (
                <Tooltip
                  key={cat}
                  title="We’re working to bring this to you soon."
                  placement="top"
                  enterTouchDelay={0}
                  leaveTouchDelay={3000}
                  disableInteractive
                >
                  <span>
                    {/* wrap MenuItem inside span so tooltip can attach */}
                    <MenuItem value={cat} disabled>
                      {cat}
                    </MenuItem>
                  </span>
                </Tooltip>
              ) : (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              )
            )}
          </Select>

          <IconButton
            onClick={sendMessage}
            disabled={!input.trim() || isThinking}
            sx={{
              height: '30px',
              display: 'flex',
              pl: 1.25,
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {input.trim() ? (
              isThinking ? (
                <DisabledSendIcon
                  sx={{ height: '24px', width: '24px', ml: 1 }}
                />
              ) : (
                <Icon sx={{ height: '24px', width: '24px', ml: 1 }} />
              )
            ) : (
              <MicIcon
                sx={{ height: '24px', width: '24px', marginLeft: '5px' }}
              />
            )}
          </IconButton>
        </Box>
      </Paper>
      {!secondaryMode && chipsContainer}
    </Box>
  );
};

const Chat = ({
  messages,
  input,
  sendMessage,
  setInput,
  selectedChatId,
  setMessages,
  handleTokenConfirmation,
  isThinking,
  handleLike,
}) => {
  const location = useLocation();

  useEffect(() => {
    if (location.state?.prefill) {
      setInput(location.state.prefill);
    }
  }, [location.state]);

  const sendMessageWrapper = async () => {
    await sendMessage();
  };

  const [category, setCategory] = useState('Marriage');

  const handleTemplateClick = (text) => {
    setInput(text);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <StyledChatWindow>
      <Box
        sx={{
          width: '100%',
          px: { xs: 1, sm: 2 },
          pt: { xs: 2, sm: 3 },
          position: 'relative',
          flex: 1,
          maxHeight: { xs: '100vh', sm: '90vh' },
          overflow: { xs: 'auto', sm: 'hidden' },
        }}
      >
        {!(messages == undefined || messages.length === 0) && (
          <Stack
            spacing={2}
            sx={{
              px: 2,
              py: 3,
              height: isMobile ? 'calc(100% - 150px)' : 'calc(100% - 200px)',
              overflowY: 'auto',
            }}
            flexDirection={'column-reverse'}
          >
            {isThinking && (
              <Box display="flex" flexDirection="row" alignItems="flex-start">
                <Avatar
                  src={theme.palette.mode === 'dark' ? LogoWhite : Logo}
                  alt="logo"
                  sx={{
                    width: 40,
                    height: 40,
                    mr: 1,
                    mt: 2,
                    p: '8px',
                    backgroundColor:
                      theme.palette.mode === 'dark' ? '#000000' : '#e8e8e8',
                    border:
                      theme.palette.mode === 'dark'
                        ? '1px solid #2a2a2a'
                        : '1px solid #bababa',
                    opacity: '70%',
                  }}
                  slotProps={{
                    img: {
                      style: {
                        objectFit: 'contain', // or 'cover'
                      },
                    },
                  }}
                />
                <TypingIndicator />
              </Box>
            )}
            {messages != null &&
              messages.map((_, idx) => {
                const message = messages[messages.length - 1 - idx];
                return (
                  <Box
                    key={idx}
                    display="flex"
                    flexDirection={
                      message.role === 'user' ? 'row-reverse' : 'row'
                    }
                    alignItems="flex-start"
                    justifyContent="flex-start"
                  >
                    {message.role !== 'user' && (
                      <Avatar
                        src={theme.palette.mode === 'dark' ? LogoWhite : Logo}
                        alt="logo"
                        sx={{
                          width: 40,
                          height: 40,
                          mr: 1,
                          mt: 2,
                          p: '8px',
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? '#000000'
                              : '#e8e8e8',
                          border:
                            theme.palette.mode === 'dark'
                              ? '1px solid #2a2a2a'
                              : '1px solid #bababa',
                          opacity: '70%',
                        }}
                        slotProps={{
                          img: {
                            style: {
                              objectFit: 'contain', // or 'cover'
                            },
                          },
                        }}
                      />
                    )}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1,
                        mt: 1,
                        bgcolor:
                          message.role === 'user'
                            ? 'transparent'
                            : 'transparent',
                        borderRadius: '4px',
                        border:
                          message.role === 'user'
                            ? theme.palette.mode === 'dark'
                              ? '1px solid white'
                              : '1px solid black'
                            : '',
                        maxWidth: {
                          xs: '90%',
                          sm: '80%',
                          md: '75%',
                          lg: '70%',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          fontFamily: `Inter, sans-serif`,
                          whiteSpace: 'pre-line',
                          color:
                            theme.palette.mode === 'dark'
                              ? '#bababa'
                              : '#454545',
                          textAlign: 'left',
                          width: '100%',
                          '& p': {
                            margin: '0 0 0px 0',
                            fontWeight: '500',
                          },
                          '& li': {
                            marginBottom: '0px',
                            fontWeight: '500',
                          },
                          '& strong': {
                            fontWeight:
                              theme.palette.mode === 'dark' ? 700 : 700,
                            color:
                              theme.palette.mode === 'dark'
                                ? '#ffffffcc'
                                : '#454545',
                          },
                          '& ul, & ol': {
                            margin: '0 0',
                            paddingLeft: '20px',
                            fontWeight: '500',
                          },
                          '& blockquote': {
                            borderLeft: '4px solid #ccc',
                            margin: '0 0',
                            paddingLeft: '16px',
                            fontStyle: 'italic',
                            fontWeight: '500',
                          },
                          '& code': {
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? '#2a2a2a'
                                : '#f5f5f5',
                            borderRadius: '3px',
                            fontFamily: 'monospace',
                            fontWeight: '500',
                          },
                          '& pre': {
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? '#2a2a2a'
                                : '#f5f5f5',
                            borderRadius: '4px',
                            overflow: 'auto',
                            margin: '0 0',
                            fontWeight: '500',
                          },
                        }}
                      >
                        {message.role !== 'user' ? (
                          <ReactMarkdown>{message.content}</ReactMarkdown>
                        ) : (
                          <p>{message.content}</p>
                        )}
                      </Box>

                      {message.role !== 'user' &&
                        !message.requiresConfirmation && (
                          <Box mt={1}>
                            <ReactionBar
                              likes={message.likes}
                              handleLike={handleLike}
                              messageId={message._id}
                              messageContent={message.content}
                            />
                          </Box>
                        )}

                      {message.requiresConfirmation && (
                        <Box display="flex" gap={1} mt={2}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleTokenConfirmation(true)}
                            sx={{
                              color:
                                theme.palette.mode === 'dark'
                                  ? '#000000'
                                  : '#bababa',
                              bgcolor:
                                theme.palette.mode === 'dark'
                                  ? '#bababa'
                                  : '#000000',
                              '&:hover': {
                                bgcolor:
                                  theme.palette.mode === 'dark'
                                    ? '#bababa'
                                    : '#000000',
                              },
                              textTransform: 'none',
                              borderRadius: '8px',
                            }}
                          >
                            Yes, proceed ({message.tokensRequired} tokens)
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleTokenConfirmation(false)}
                            sx={{
                              borderColor:
                                theme.palette.mode === 'dark'
                                  ? '#bababa'
                                  : '#000000',
                              color:
                                theme.palette.mode === 'dark'
                                  ? '#bababa'
                                  : '#000000',
                              textTransform: 'none',
                              borderRadius: '8px',
                            }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                );
              })}
          </Stack>
        )}
        {messages == undefined || messages.length === 0 ? (
          <Box>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                textAlign: 'center',
                fontSize: { xs: '18px', sm: '20px', md: '24px' },
                fontWeight: 500,
                background:
                  'linear-gradient(to right, #EC5569, #D78100, #8A45D9)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                height: 'auto',
                margin: {
                  xs: '80px auto 0',
                  sm: '100px auto 0',
                  md: '153px auto 0',
                },
                maxWidth: { xs: '280px', sm: '380px', md: '440px' },
                width: '100%',
              }}
            >
              Curious about your future? Ask me!
            </Typography>
            <InputTextBoxGroup
              input={input}
              setInput={setInput}
              category={category}
              setCategory={setCategory}
              sendMessage={sendMessageWrapper}
              handleTemplateClick={handleTemplateClick}
              Icon={SendIcon}
              secondaryMode={false}
              isThinking={isThinking}
            />
          </Box>
        ) : (
          <InputTextBoxGroup
            input={input}
            setInput={setInput}
            category={category}
            setCategory={setCategory}
            sendMessage={sendMessageWrapper}
            handleTemplateClick={handleTemplateClick}
            Icon={SendIcon}
            secondaryMode={true}
            isThinking={isThinking}
          />
        )}
      </Box>
    </StyledChatWindow>
  );
};

const StyledChatWindow = styled(Box)(({ theme }) => ({
  padding: '10px 10px',
  maxWidth: '800px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  gap: '20px',
  height: '100%',
}));

export default Chat;
